import { TreeAccordion } from '@/components/accordion/Accordion';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { Button } from '@/design-system/button';
import { featureFlags } from '@/configs/featureFlags';
import { usePrivileges } from '@/features/users/roles-privilege/hooks/usePrivileges';
import { collectSelectableFunctionIds } from '@/features/users/roles-privilege/privilegeSelection';
import { Translation } from '@/hoslog-intl';
import { Configuration } from '@/icons';
import SaveIcon from '@/icons/save-icon';
import { areSetsEqual, collectAssignedFunctionIds } from '@/lib/compareSets';
import { ApiResponse } from '@/react-query/context/ApiClientContextController';
import { useMutation } from '@/react-query/hooks/useMutation/useMutation';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/(app)/users/roles/privilege/$roleId')({
  validateSearch: (search) => ({
    roleName: String(search.roleName),
  }),
  loaderDeps: ({ search }) => ({
    roleName: search.roleName,
  }),
  loader: ({ deps: { roleName } }) => ({
    crumb: <CrumbTitle id="userManagement.rolesAndPrivileges.crumb.function" icon={Configuration} />,
    title: (
      <>
        <Translation id="userManagement.rolesAndPrivileges.page.title" />{' '}
        <span className="text-destructive-text">{roleName}</span>
      </>
    ),
  }),

  component: RouteComponent,
});

function RouteComponent() {
  const { roleId } = useParams({ from: '/(app)/users/roles/privilege/$roleId' });
  const { privileges } = usePrivileges(roleId);
  const navigate = useNavigate();
  const { mutate, isPending } = useMutation('manageRolePermission', {
    onSuccess: async (data: ApiResponse) => {
      toast.success(data.message || 'Privileges updated successfully');
      await navigate({ to: '/users/roles' });
    },
  });
  const [selectedFunctions, setSelectedFunctions] = useState<Set<string>>(new Set());

  const initialSelectedRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!privileges) return;

    const initial = collectAssignedFunctionIds(privileges);
    initialSelectedRef.current = initial;

    setSelectedFunctions(new Set(initial));
  }, [privileges]);

  const isDirty = useMemo(() => {
    const initial = initialSelectedRef.current;
    if (!initial) return false;
    return !areSetsEqual(selectedFunctions, initial);
  }, [selectedFunctions]);

  const allFunctionIds = useMemo(() => collectSelectableFunctionIds(privileges), [privileges]);
  const allSelected =
    allFunctionIds.size > 0 && Array.from(allFunctionIds).every((functionId) => selectedFunctions.has(functionId));

  const handleSaveChanges = () => {
    const functionIds = Array.from(selectedFunctions);

    mutate({ roleId, functionIds });
  };
  return (
    <div className="space-y-4 relative">
      <div className="absolute -top-[66px] right-0 flex items-center gap-2">
        {featureFlags.rolePrivilegeBulkActions && (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSelectedFunctions(new Set(allFunctionIds))}
              disabled={allFunctionIds.size === 0 || allSelected || isPending}
            >
              <Translation id="userManagement.rolesAndPrivileges.button.selectAll" />
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSelectedFunctions(new Set())}
              disabled={selectedFunctions.size === 0 || isPending}
            >
              <Translation id="userManagement.rolesAndPrivileges.button.clearAll" />
            </Button>
          </>
        )}
        <Button
          className="border-foreground"
          type="button"
          onClick={handleSaveChanges}
          disabled={!isDirty || isPending}
        >
          <SaveIcon />
          <Translation id="userManagement.rolesAndPrivileges.button.saveChanges" />
        </Button>
      </div>

      <TreeAccordion
        items={privileges}
        selectedFunctions={selectedFunctions}
        setSelectedFunctions={setSelectedFunctions}
      />
    </div>
  );
}
